import { useCallback, useEffect, useRef, useState } from 'react';
import { stripHtmlTags } from '../utils/general';
import {
	hasLowerCaseCharacter,
	hasMinimumPasswordLength,
	hasUpperCaseCharacter,
	isChecked,
	isEmailDomainAllowed,
	isNotEmpty,
	isValidEmailFormat,
} from '../utils/validate';

const getDefaultFormFieldValue = (name) => {
	if (name === 'email') {
		const url = new URL(window.location);
		if (url.searchParams.has('email')) {
			return url.searchParams.get('email');
		}
	}
	return '';
};

export const useRegistration = ({ data, getLabel, onFormSubmit }) => {
	const errorMessageDelay = 1000;
	const errorMessageTimeouts = useRef({}); // key: field name, value: timeout id

	const [registrationStep, setRegistrationStep] = useState(0);
	const [formFields, setFormFields] = useState([
		{
			step: 0,
			label: getLabel('form_field_label_email'),
			name: 'email',
			type: 'email',
			required: true,
			value: getDefaultFormFieldValue('email'),
			validation: [
				{ callback: isNotEmpty, message: getLabel('form_field_error_empty') },
				{ callback: isValidEmailFormat, message: getLabel('form_field_error_email') },
				{ callback: isEmailDomainAllowed, message: getLabel('form_field_error_blacklisted') },
			],
			visibleErrors: false,
			sendToHubspot: true,
		},
		{
			step: 1,
			label: getLabel('form_field_label_first_name'),
			name: 'firstname',
			type: 'text',
			required: true,
			value: getDefaultFormFieldValue('firstname'),
			validation: [{ callback: isNotEmpty, message: getLabel('form_field_error_empty') }],
			visibleErrors: false,
			sendToHubspot: true,
		},
		{
			step: 1,
			label: getLabel('form_field_label_last_name'),
			name: 'lastname',
			type: 'text',
			required: true,
			value: getDefaultFormFieldValue('lastname'),
			validation: [{ callback: isNotEmpty, message: getLabel('form_field_error_empty') }],
			visibleErrors: false,
			sendToHubspot: true,
		},
		{
			step: 1,
			label: getLabel('form_field_label_phone'),
			name: 'phone',
			type: 'tel',
			required: false,
			value: getDefaultFormFieldValue('phone'),
			visibleErrors: false,
			sendToHubspot: true,
		},
		{
			step: 1,
			label: getLabel('form_field_label_password'),
			name: 'password',
			type: 'password',
			required: true,
			value: getDefaultFormFieldValue('password'),
			validation: [
				{ callback: hasMinimumPasswordLength, directive: getLabel('password_need_length'), message: getLabel('form_field_error_invalid') },
				{ callback: hasLowerCaseCharacter, directive: getLabel('password_need_lowercase'), message: getLabel('form_field_error_invalid') },
				{ callback: hasUpperCaseCharacter, directive: getLabel('password_need_uppercase'), message: getLabel('form_field_error_invalid') },
			],
			visibleErrors: false,
			sendToHubspot: false,
		},
		{
			step: 1,
			label: getLabel('registration_consent_text'),
			name: 'tosConsentText',
			type: 'switch',
			required: true,
			value: stripHtmlTags(getLabel('registration_consent_text')),
			checked: false,
			validation: [{ callback: isChecked, message: getLabel('form_field_error_checkbox') }],
			visibleErrors: false,
			sendToHubspot: false,
		},
	]);

	const getFormData = () => {
		return formFields.reduce((data, field) => {
			data[field.name] = field.value;
			return data;
		}, {});
	};

	const getHubspotFormData = () => {
		return formFields.reduce((data, field) => {
			if (field?.sendToHubspot !== true) return data;
			data.push({
				name: field.name,
				value: field.value,
			});
			return data;
		}, []);
	};

	// --- Validation ---
	const getFieldErrorMessages = useCallback(
		(name) => {
			const field = formFields.find((field) => field.name === name);
			if (!field || !field.validation) return [];
			return field.validation.reduce((messages, { callback, message }) => {
				if (!callback({ checked: field?.checked, value: field.value, blacklist: data.blacklist })) {
					messages.push(message);
				}
				return messages;
			}, []);
		},
		[formFields, data.blacklist],
	);

	const fieldIsValid = useCallback(
		(name) => {
			const field = formFields.find((field) => field.name === name);
			if (!field || !field.validation) return true;
			return field.validation.every(({ callback }) => callback({ checked: field?.checked, value: field.value, blacklist: data.blacklist }));
		},
		[formFields, data.blacklist],
	);

	const currentStepFieldsAreValid = useCallback(() => {
		const currentStepFields = formFields.filter((field) => field.step === registrationStep);
		return currentStepFields.every((field) => {
			if (!field.validation) return true;
			return fieldIsValid(field.name);
		});
	}, [formFields, registrationStep, fieldIsValid]);

	// --- Navigation ---
	const stepHasFields = useCallback((step) => formFields.some((field) => field.step === step), [formFields]);

	const nextStep = useCallback(() => {
		if (currentStepFieldsAreValid()) {
			if (stepHasFields(registrationStep + 1)) {
				// If the next step has fields, just increment the step
				setRegistrationStep((prevStep) => prevStep + 1);
			} else {
				onFormSubmit({ getFormData, getHubspotFormData });
			}
		} else {
			// Set visible errors for all fields in the current step
			setFormFields((prevFields) => prevFields.map((field) => (field.step === registrationStep ? { ...field, visibleErrors: true } : field)));
		}
	}, [currentStepFieldsAreValid]);

	const previousStep = useCallback(() => {
		setRegistrationStep((prevStep) => (prevStep > 0 ? prevStep - 1 : 0));
	}, []);

	// --- Field Change Handler ---
	const handleFieldChange = useCallback(
		(event) => {
			const { name, value, type, checked } = event.target;

			// Clear previous timeout for this field only
			if (errorMessageTimeouts.current[name]) {
				clearTimeout(errorMessageTimeouts.current[name]);
			}

			// Immediately hide errors when user starts typing
			setFormFields((prevFields) =>
				prevFields.map((field) =>
					field.name === name
						? {
								...field,
								value,
								visibleErrors: false,
								...(type === 'checkbox' ? { checked } : {}),
							}
						: field,
				),
			);

			// Start a timeout to show errors after delay (for this field only)
			errorMessageTimeouts.current[name] = setTimeout(() => {
				setFormFields((prevFields) => prevFields.map((field) => (field.name === name ? { ...field, visibleErrors: true } : field)));
			}, errorMessageDelay);
		},
		[errorMessageDelay],
	);

	// --- Cleanup: clear all timeouts on unmount ---
	useEffect(() => {
		return () => {
			Object.values(errorMessageTimeouts.current).forEach(clearTimeout);
		};
	}, []);

	return {
		registrationStep,
		formFields,
		fieldIsValid,
		getFieldErrorMessages,
		handleFieldChange,
		nextStep,
		previousStep,
	};
};
